import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Search, RefreshCw, CalendarClock, DoorOpen, ScanLine, FileText, Stamp,
    AlertTriangle, Clock, Loader2, Lock, ShieldAlert, X, Siren, Timer,
    Stethoscope, CalendarDays, Radiation, Images, Beaker, MapPin,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
// The default export of hooks/useAxiosAuth is a FACTORY that builds an axios
// instance, not a React hook — it calls nothing from React. Imported under its
// own name it trips eslint react-hooks/rules-of-hooks. Renaming it at the import
// is the whole fix, and it is what every other caller in this codebase does.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';
import useSession from '../../hooks/useSession';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { hospitalPatientCode } from '../../utils/patientCode';
import { formatClock, formatTime } from '../../utils/clock';
import {
    getRadiologyWorklist, getSlots,
    scheduleStudy, arriveStudy, performStudy,
} from '../../redux/radiologySlice';

const MySwal = withReactContent(Swal);

// THE IMAGING WORKLIST — what the department owes today, and what has to happen
// to each of it next.
//
// A worklist is a QUERY, not a table. There is no imaging_worklists row in the
// backend and there must never be one: the moment "what is waiting" is stored
// rather than derived it can disagree with the studies it claims to summarise,
// and a department reading a stale list leaves a patient sitting in a corridor.
// Every bucket below is a filter over imaging_studies as they stand this second.
//
// THE FIVE BUCKETS ARE THE FIVE PLACES A PATIENT CAN PHYSICALLY BE, and each one
// ends with a different person doing a different thing:
//   ordered   -> a scan has been asked for and no machine holds a minute of time
//                for it. Somebody has to give the patient an appointment.
//   scheduled -> a machine and an instant are held. The patient is elsewhere.
//   arrived   -> the patient is in the department, waiting to go in.
//   performed -> the pictures exist. A radiologist owes a report.
//   reported  -> a draft exists and a REGISTERED radiologist has to sign it.
// They are statuses rather than the backend's coarser queue ids because this
// screen draws one button per row, and 'scheduled' and 'arrived' owe different
// acts even though the department's queue lumps them together as "to perform".
//
// THE ACCESSION NUMBER IS THE LOUDEST THING ON EVERY ROW, and it is bigger here
// than it is in the laboratory for a physical reason: it is written by hand on a
// film envelope, read out down a telephone to the console, and typed into the
// modality worklist. Near-black monospace on white is what wins that contest
// against a row that also carries a name, a procedure and a clock. Deliberately
// not in the brand palette — it is a wayfinding label, not chrome, and teal here
// would make teal mean "important" instead of "this is the app".
//
// A ROW WITH NO ACCESSION NUMBER IS NOT A FAULT. The number is minted when the
// machine is booked, not when the scan is ordered, so an unbooked request has
// none — an order that is never scheduled must not burn a number out of the
// year's register and leave a hole an inspector asks about. "Not booked" is the
// state, and it is said in words.
//
// PC-PNDT IS ON THE ROW, NOT ONLY IN THE REFUSAL. Performing an obstetric or
// pelvic ultrasound on a female patient without a completed Form F is a criminal
// offence — imprisonment and the sealing of the machine, not a fine. The backend
// refuses it (PNDT_FORM_REQUIRED) and this screen refuses it one step earlier,
// with the reason in a full sentence and a link that goes straight to the form,
// because a radiographer who discovers the requirement with a patient on the
// table is a radiographer who will be tempted to scan first and file later.
// `pndtRequired` and `pndtCleared` are computed by the server from the procedure
// and the patient with the same function the perform gate uses, so a green tick
// here and a pass at the console are the same fact.
//
// WHAT MAY HAPPEN NEXT IS THE SERVER'S ANSWER, NOT THIS FILE'S. Every row
// carries `nextStatuses` straight from services/radiology/imagingService's
// transition table, and the buttons below are drawn from it intersected with
// what the signed-in user may do. A second copy of that table in the browser is
// how a screen ends up offering "Reschedule" on an arrived patient — legal in
// the client, refused by the server, and read by the department as a bug in the
// software rather than a rule.
//
// PERMISSIONS ARE NOT ENFORCED HERE. requireTenantFeature('radiology') and
// requirePermission guard every route and the model layer scopes every read to
// one hospital. The can() checks below only decide whether a control is drawn,
// and when it is not they SAY SO IN WORDS: a receptionist who may book but not
// perform needs to learn that from the screen, not from a 403 after they have
// filled in a modal. A DOCTOR holds no staff permission at all (see the header
// of api/middleware/requirePermission.js) and so sees this list without the
// department's own buttons, which is exactly right: booking a machine and
// recording an exposure are acts BY the department, recorded against a named
// member of it.
//
// THE DAY FILTER APPLIES TO THE DAY'S LIST AND NOTHING ELSE. A booked scan lives
// on its slot, so "everything on Tuesday" is the front desk's actual list and
// the date is on by default for 'scheduled' and 'arrived'. The other three
// buckets are queues of OWED work — a request nobody has given a time to, a scan
// performed on Friday that still has no report — and a date filter over those is
// how a backlog hides itself. They default to the whole outstanding window, and
// the toggle says which is in force rather than leaving somebody to guess why a
// row they know exists is not on screen.
//
// ---------------------------------------------------------------------------
// THE API THIS SCREEN CONSUMES, AND WHERE IT LIVES.
//
// Every act goes through redux/radiologySlice.js — getRadiologyWorklist,
// getSlots, scheduleStudy, arriveStudy, performStudy — and none of those paths
// is spelled out in this file. One caller per endpoint is what stops two screens
// drifting into two spellings of one path, which is a defect this codebase has
// already paid for once in the laboratory.
//
// THE ONE EXCEPTION IS THE MACHINE LIST, and it is deliberate rather than an
// omission. The radiology MASTERS have no slice at all — ImagingEquipment.jsx and
// ImagingProcedures.jsx read them through a plain authed axios instance, because
// registering a slice means touching src/store.js, which those screens ship ahead
// of. The slot picker needs the fleet to offer a machine, so it reads the same
// endpoint the same way rather than inventing a second convention for one GET.
// It is a read with no side effect: when it fails, nothing on the list is wrong,
// and the picker says so where somebody is actually trying to book.
//
// A worklist row, with every field this file reads:
//   accessionNo, studyId, status, nextStatuses[],
//   orderId, priority ('routine'|'urgent'|'stat'), orderedAt, clinicalNotes,
//   procedureId, procedureName, modality, bodyPart, durationMins, preparation,
//   requiresContrast,
//   equipmentId, equipmentName, room,
//   scheduledAt, arrivedAt, performedAt, performedBy, contrastUsed,
//   imageCount, imagesHeld,
//   patientId, patientName, patientAge, patientGender, doctorName,
//   pndtRequired, pndtFormNo, pndtCleared
// Everything except studyId is read defensively: a row missing its accession
// number renders as "not booked" rather than as a crash.
// ---------------------------------------------------------------------------

const REFRESH_MS = 20000;
const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 25;

// The backend's own window when no date is sent (WORKLIST_WINDOW_DAYS). Quoted on
// screen when somebody searches, because a search that silently covers three
// months and not the whole archive has to say so — otherwise a study from last
// year reads as deleted.
const BACKLOG_WINDOW_DAYS = 90;

// Where the statutory form lives. One constant, because it is linked from four
// places on this screen and a mistyped path here is a dead end reached by
// somebody who has a patient waiting.
const formFPath = (studyId) => `/radiology/form-f/${studyId}`;

// The machine list, read exactly as ImagingEquipment.jsx reads it — see the
// header on why this one GET does not go through the slice.
const EQUIPMENT_API = '/radiology/equipment';

// A department owns machines in the tens, never in the thousands, so the fleet is
// fetched in one go. A picker split across pages is one where the free scanner is
// on page two.
const FLEET_LIMIT = 200;

// The buckets whose rows are anchored on a booked slot, and therefore the only
// ones a day filter belongs on by default. See the header.
const DAY_LIST_BUCKETS = ['scheduled', 'arrived'];

const BUCKETS = [
    { id: 'ordered', icon: CalendarClock, fallback: 'To book' },
    { id: 'scheduled', icon: CalendarDays, fallback: 'Booked' },
    { id: 'arrived', icon: DoorOpen, fallback: 'In the department' },
    { id: 'performed', icon: ScanLine, fallback: 'To report' },
    { id: 'reported', icon: FileText, fallback: 'To sign' },
];

const BUCKET_HINTS = {
    ordered: 'Asked for, with no machine time held. Somebody has to give the patient a time.',
    scheduled: 'A machine and an instant are held. The patient has not arrived yet.',
    arrived: 'The patient is in the department, waiting to go in.',
    performed: 'The pictures exist. A radiologist owes a report.',
    reported: 'A draft exists. A registered radiologist has to sign it.',
};

// Mirrors the modality ENUM on imaging_procedures / imaging_equipment. A filter
// rather than a decoration: an X-ray room and an MRI suite are different rooms
// with different staff, and each wants only its own list.
const MODALITIES = ['xray', 'ultrasound', 'ct', 'mri', 'mammography', 'dexa', 'echo', 'ecg', 'other'];

// stat and urgent are the only two that change what anybody does. 'routine' gets
// no badge at all — a label on every row is a label on no row.
// `rail` paints a card's left edge and `railBorder` a table row's: the same
// stripe drawn by the two mechanisms each layout can honour. A background-
// coloured <span> in a <td> does not stretch to the row height in any browser
// worth supporting, and a border does not exist on a flex child.
const PRIORITY_STYLES = {
    stat: {
        badge: 'bg-red-600 text-white',
        rail: 'bg-red-600',
        railBorder: 'border-red-600',
        row: 'bg-red-50/40',
        label: 'STAT',
    },
    urgent: {
        badge: 'bg-amber-500 text-white',
        rail: 'bg-amber-500',
        railBorder: 'border-amber-500',
        row: '',
        label: 'URGENT',
    },
};

// Why a slot cannot be taken, in words the person at the desk can repeat to the
// patient standing in front of them. `taken` is handled separately because it
// carries the accession number holding the machine, which is the answer to
// "well, who has got it then".
const SLOT_REASONS = {
    past: 'Gone',
    overruns_close: 'Runs past closing',
    equipment: 'Machine unavailable',
};

const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Local calendar day as 'YYYY-MM-DD'. Never via toISOString(), which is UTC and
 *  hands back yesterday for anybody east of Greenwich after teatime — on a day
 *  list that reads as an empty department. */
const todayLocal = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// The one clock format ("2:30 PM", utils/clock); '' when not a date.
const fmtClock = (iso) => formatTime(iso);

const fmtDay = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
};

/** Minutes from now until `iso`. Negative once the slot is in the past. */
const minutesUntil = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.round((d.getTime() - Date.now()) / 60000);
};

const fmtDelay = (mins) => {
    const abs = Math.abs(mins);
    if (abs < 60) return `${abs}m`;
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
};

/* ------------------------------------------------------------------ *
 * Presentational pieces
 *
 * Outside the component on purpose: none of them reads page state, and keeping
 * them inside would rebuild every one on each keystroke in the search box, which
 * is the one place on this screen where that cost is felt.
 * ------------------------------------------------------------------ */

/**
 * The accession number, drawn once and drawn the same everywhere.
 *
 * Bigger than the laboratory's, because this one is written by hand on a film
 * envelope and read out to a console across a room. When there is none the
 * answer is not '-': a study with no accession number has no machine time held,
 * and "not booked" is the finding rather than a missing value.
 */
function AccessionBadge({ accessionNo, testId, size = 'lg' }) {
    const { t } = useTranslation();
    if (!accessionNo) {
        return (
            <span
                data-testid={testId}
                title={t('imagingworklist.no_accession_hint', 'The number is minted when the machine is booked, so an unbooked request has none')}
                className="inline-flex items-center gap-1 px-2.5 py-2 rounded-xl bg-amber-100 border-2 border-dashed border-amber-400 text-amber-700 text-[11px] font-bold uppercase tracking-wide"
            >
                <CalendarClock className="w-3.5 h-3.5" />
                {t('imagingworklist.not_booked', 'Not booked')}
            </span>
        );
    }
    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center px-3 py-2 rounded-xl bg-slate-900 text-white shadow-md font-mono leading-none font-black tracking-wider whitespace-nowrap ${size === 'xl' ? 'text-2xl' : 'text-xl'}`}
        >
            {accessionNo}
        </span>
    );
}

/** STAT and URGENT. Word + colour + a filled shape, never colour alone: roughly
 *  one man in twelve cannot separate the red from the amber. */
function PriorityBadge({ priority }) {
    const style = PRIORITY_STYLES[String(priority || '').toLowerCase()];
    if (!style) return null;
    return (
        <span
            data-testid={`priority-${priority}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-black uppercase tracking-widest ${style.badge}`}
        >
            {priority === 'stat' ? <Siren className="w-3 h-3" /> : <Timer className="w-3 h-3" />}
            {style.label}
        </span>
    );
}

/**
 * Whether the PC-PNDT Act applies to this study, and whether its form exists.
 *
 * Three states and all three are drawn, because "no badge" would make a scan the
 * Act covers look identical to one it does not. Cleared carries the form number:
 * that string is what an inspection asks for by name.
 */
function PndtBadge({ row }) {
    const { t } = useTranslation();
    if (!row.pndtRequired) return null;
    if (row.pndtCleared) {
        return (
            <span
                data-testid={`pndt-ok-${row.studyId}`}
                title={t('imagingworklist.pndt_ok_hint', 'Form F is complete: the declaration is accepted, the performing doctor is registered and the referrer is named')}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide"
            >
                <Stamp className="w-3 h-3" />
                {row.pndtFormNo || t('imagingworklist.pndt_done', 'Form F')}
            </span>
        );
    }
    return (
        <span
            data-testid={`pndt-missing-${row.studyId}`}
            title={t('imagingworklist.pndt_missing_hint', 'PC-PNDT: this scan cannot be recorded as performed until Form F is complete')}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-black uppercase tracking-wide"
        >
            <ShieldAlert className="w-3 h-3" />
            {t('imagingworklist.pndt_required', 'Form F required')}
        </span>
    );
}

/**
 * A control the signed-in user may not use.
 *
 * Drawn rather than hidden, with the reason on it. Hiding it leaves somebody
 * staring at a row nothing can be done to and no way to find out why; the useful
 * answer is "you can book a machine but not record an exposure — that is
 * radiology.study.perform", and the only place that sentence fits is here.
 */
function LockedAction({ label, reason, testId }) {
    return (
        <span
            data-testid={testId}
            title={reason}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-400 text-xs font-semibold cursor-not-allowed"
        >
            <Lock className="w-3.5 h-3.5" />
            {label}
        </span>
    );
}

/* ------------------------------------------------------------------ *
 * Booking a machine
 * ------------------------------------------------------------------ */

/**
 * THE SLOT PICKER. One machine, one day, every start time it has.
 *
 * THE WHOLE GRID IS DRAWN, NOT ONLY THE FREE TIMES. A list of free slots cannot
 * answer "why can I not have 14:40", so the clerk telephones the department to
 * ask; and a grid that silently shortens as the day fills is indistinguishable
 * from one that failed to load. Every start carries whether it is available and,
 * when it is not, the reason — including the accession number of the study
 * holding the machine, which is the answer to "well, who has got it then".
 *
 * THE GRID IS THE MACHINE'S, NOT THE DEPARTMENT'S. Starts come from that
 * equipment's own openTime / closeTime / slotMinutes, because a busy X-ray room
 * turns a patient round every ten minutes while an MRI is booked in half hours.
 * None of that is computed here: GET /slots returns it, laid out by the same
 * service the booking endpoint validates against, so a time offered on this
 * screen cannot be refused at the counter with the patient present.
 *
 * A MACHINE WHOSE REGISTRATION HAS LAPSED GREYS OUT ITS WHOLE DAY and says which
 * certificate and when it expired. An empty grid would read as "fully booked",
 * and nobody chases a renewal they have not been told about — while operating an
 * unregistered X-ray unit is the hospital's licence, not a warning banner.
 *
 * ONLY MACHINES OF THE RIGHT MODALITY ARE OFFERED, because the backend refuses
 * the rest by name (EQUIPMENT_MODALITY_MISMATCH) and a picker that lists a CT
 * scanner for an ultrasound is a picker that wastes somebody's third attempt.
 * When the department has registered none of that modality the list opens up
 * rather than showing nothing, with the reason said out loud — an empty picker
 * is indistinguishable from a broken one.
 */
function SlotPicker({ open, row, equipment, equipmentError, busy, tenantId, onClose, onConfirm }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [equipmentId, setEquipmentId] = useState('');
    const [date, setDate] = useState(() => todayLocal());
    const [grid, setGrid] = useState(null);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Machines that can actually perform this examination. `row.modality` is the
    // study's own, snapshotted from the procedure at ordering.
    const matching = useMemo(() => {
        if (!row?.modality) return equipment;
        const same = equipment.filter((e) => String(e.modality) === String(row.modality));
        return same.length ? same : equipment;
    }, [equipment, row]);

    const noneOfModality = Boolean(
        row?.modality && equipment.length && !equipment.some((e) => String(e.modality) === String(row.modality))
    );

    useEffect(() => {
        if (!open || !row) return;
        // Reopening on a different study must not inherit the previous one's
        // machine and time — a slot picked for somebody else, confirmed by a
        // finger already moving, is the booking nobody can explain afterwards.
        setSelected(null);
        setGrid(null);
        setError(null);
        setDate(row.scheduledAt ? String(row.scheduledAt).slice(0, 10) : todayLocal());
        setEquipmentId(row.equipmentId || (matching[0] ? matching[0].equipmentId : ''));
    }, [open, row, matching]);

    useEffect(() => {
        if (!open || !equipmentId || !date) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                // studyId is passed rather than the bare procedure: it is what
                // lets the server block every slot the examination's own duration
                // spans, apply the PC-PNDT question to THIS patient, and — when
                // this is a reschedule — not report the study's own current slot
                // as taken, which would make the only time it cannot be moved to
                // the one it is already in.
                const body = await dispatch(getSlots({
                    equipmentId,
                    date,
                    studyId: row?.studyId,
                    tenantId,
                })).unwrap();
                if (cancelled) return;
                setGrid(body || null);
                setSelected(null);
            } catch (err) {
                if (cancelled) return;
                setGrid(null);
                setError(extractErrorMessage(err, t('imagingworklist.slots_error', 'Could not load this machine’s day')));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [dispatch, open, equipmentId, date, row, tenantId, t]);

    if (!open || !row) return null;

    const slots = grid?.slots || [];
    const freeCount = toNum(grid?.freeCount);

    return (
        <div
            data-testid="slot-picker"
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            role="dialog"
            aria-modal="true"
        >
            <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
                <div className="flex items-start justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                    <div>
                        <h2 className="text-lg font-bold">
                            {row.scheduledAt
                                ? t('imagingworklist.reschedule_title', 'Move this scan')
                                : t('imagingworklist.schedule_title', 'Book a machine')}
                        </h2>
                        <p className="text-xs text-white/80 mt-0.5">
                            {[row.patientName || t('common.patient_label', 'Patient'), row.procedureName].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="slot-picker-close"
                        onClick={onClose}
                        className="p-1 rounded-lg hover:bg-white/20 cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-xs font-semibold text-slate-600">
                                {t('imagingworklist.machine', 'Machine')}
                            </span>
                            <select
                                data-testid="slot-equipment"
                                value={equipmentId}
                                onChange={(e) => setEquipmentId(e.target.value)}
                                className="tt-select mt-1 w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            >
                                <option value="">{t('imagingworklist.pick_machine', 'Pick a machine…')}</option>
                                {matching.map((e) => (
                                    <option key={e.equipmentId} value={e.equipmentId}>
                                        {[e.name, e.room, e.modality].filter(Boolean).join(' · ')}
                                    </option>
                                ))}
                            </select>
                            {noneOfModality && (
                                <span className="text-[11px] text-amber-700 mt-1 inline-block">
                                    {t(
                                        'imagingworklist.no_matching_machine',
                                        'No {{modality}} machine is registered, so every machine is listed. A booking on one that cannot perform this examination will be refused.',
                                        { modality: row.modality }
                                    )}
                                </span>
                            )}
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-slate-600">{t('imagingworklist.day', 'Day')}</span>
                            <input
                                data-testid="slot-date"
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="mt-1 w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            />
                        </label>
                    </div>

                    {/* The machine list failed, so nothing can be booked. Said
                        plainly: an empty picker with no explanation reads as a
                        broken screen and the booking is taken on paper. */}
                    {equipmentError && !equipment.length && (
                        <div data-testid="equipment-error" className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                {t(
                                    'imagingworklist.equipment_error',
                                    'The department’s machines could not be loaded, so there is nothing to book onto. An administrator registers them under the radiology masters.'
                                )}
                            </span>
                        </div>
                    )}

                    {/* What the patient has to do before they come. Prep is the
                        commonest reason a booked scan is wasted — a full bladder
                        for a pelvic ultrasound, six hours nil by mouth for an
                        abdominal CT — so it is put in front of whoever is giving
                        out the time, not left on the procedure master. */}
                    {row.preparation && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-[#4ABDBD]/10 text-[#1f6b6b] text-xs">
                            <Beaker className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                <strong className="font-semibold">{t('imagingworklist.preparation', 'Preparation')}: </strong>
                                {row.preparation}
                            </span>
                        </div>
                    )}

                    {row.pndtRequired && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs">
                            <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                {t(
                                    'imagingworklist.pndt_booking_note',
                                    'This examination is within the PC-PNDT Act. It may be booked now, but it cannot be recorded as performed until Form F is complete, and only a machine registered under the Act may be used.'
                                )}
                            </span>
                        </div>
                    )}

                    {/* The refusal the booking endpoint WOULD throw, as a value:
                        which certificate, and when it expired. */}
                    {grid?.blocked && (
                        <div data-testid="slot-blocked" className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border-2 border-red-300 text-red-800 text-sm">
                            <Radiation className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-bold">{t('imagingworklist.machine_unusable', 'This machine cannot be booked')}</div>
                                <p className="text-xs mt-0.5">{grid.blocked.message}</p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center gap-2 py-8 text-[#2D8A8A] text-sm">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('imagingworklist.loading_slots', 'Reading the machine’s day…')}
                        </div>
                    ) : grid ? (
                        <>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                                <span>
                                    {t('imagingworklist.open_hours', 'Open {{from}}–{{to}}', {
                                        from: formatClock(grid.openTime),
                                        to: formatClock(grid.closeTime),
                                    })}
                                </span>
                                <span>
                                    {t('imagingworklist.holds_for', 'This examination holds the machine for {{n}} minutes', {
                                        n: toNum(grid.durationMins) || toNum(grid.slotMinutes),
                                    })}
                                </span>
                                <span className={freeCount ? 'text-[#2D8A8A] font-semibold' : 'text-red-600 font-semibold'}>
                                    {t('imagingworklist.free_count', '{{n}} free', { n: freeCount })}
                                </span>
                            </div>

                            <div data-testid="slot-grid" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                                {slots.map((slot) => {
                                    const isSelected = selected && selected.start === slot.start;
                                    const held = slot.reason === 'taken' ? slot.heldBy : null;
                                    const why = held
                                        ? t('imagingworklist.slot_taken_by', 'Held by {{no}}', {
                                            no: held.accessionNo || '—',
                                        })
                                        : t(`imagingworklist.slot_${slot.reason}`, SLOT_REASONS[slot.reason] || '');
                                    return (
                                        <button
                                            key={slot.start}
                                            type="button"
                                            data-testid={`slot-${slot.time}`}
                                            title={slot.available ? undefined : why}
                                            disabled={!slot.available}
                                            onClick={() => setSelected(slot)}
                                            className={`px-2 py-2 rounded-xl border text-sm font-semibold transition-colors ${
                                                isSelected
                                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white border-transparent shadow'
                                                    : slot.available
                                                        ? 'bg-white border-[#4ABDBD] text-[#2D8A8A] hover:bg-[#4ABDBD]/10 cursor-pointer'
                                                        : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                                            }`}
                                        >
                                            <span className="font-mono whitespace-nowrap">{formatClock(slot.time)}</span>
                                            {!slot.available && why && (
                                                <span className="block text-[9px] font-normal leading-tight mt-0.5 truncate">{why}</span>
                                            )}
                                        </button>
                                    );
                                })}
                                {!slots.length && (
                                    <div className="col-span-full p-6 text-center text-sm text-slate-500">
                                        {t('imagingworklist.no_slots', 'This machine has no start times left on that day.')}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="p-6 text-center text-sm text-slate-500">
                            {t('imagingworklist.pick_machine_first', 'Pick a machine to see its day.')}
                        </div>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
                    <span className="text-xs text-slate-500">
                        {selected
                            ? t('imagingworklist.selected_slot', 'Booking {{time}} on {{machine}}', {
                                time: formatClock(selected.time),
                                machine: grid?.equipmentName || equipmentId,
                            })
                            : t('imagingworklist.no_slot_selected', 'Pick a start time.')}
                    </span>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-xl border border-gray-300 text-slate-600 text-sm font-semibold hover:bg-white cursor-pointer"
                        >
                            {t('imagingworklist.cancel', 'Cancel')}
                        </button>
                        <button
                            type="button"
                            data-testid="slot-confirm"
                            disabled={!selected || busy}
                            onClick={() => onConfirm({ equipmentId, scheduledAt: selected.start, time: selected.time })}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white text-sm font-semibold shadow-lg ${!selected || busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl'}`}
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
                            {t('imagingworklist.confirm_booking', 'Book this time')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * Recording the exposure
 * ------------------------------------------------------------------ */

/**
 * PERFORM — the one that cannot be undone.
 *
 * Nothing in the transition table returns from 'performed', so a study wrongly
 * marked performed can only be rescued with hand-written SQL. The dialog
 * therefore states what is about to be recorded and against whom, and the
 * confirmation is a real button rather than a row action that fires on the first
 * click.
 *
 * CONTRAST IS A FACT ABOUT A PERSON, NOT A COPY OF THE PROCEDURE MASTER. The
 * master's requiresContrast is a planning flag; what actually went into this
 * patient has to be traceable to the agent that caused a reaction, which is why
 * naming the agent is required as soon as the box is ticked — the backend
 * refuses it as CONTRAST_AGENT_REQUIRED and this asks for it before the round
 * trip, with a patient still on the table.
 *
 * THE RADIOGRAPHER IS NOT A FIELD HERE. performedBy comes from the verified
 * token server-side: it is half of radiology's two-person rule (the person who
 * makes the pictures is not the registered person who interprets them), and a
 * name typed into a box is not an attribution.
 */
function PerformModal({ open, row, busy, onClose, onConfirm }) {
    const { t } = useTranslation();
    const [contrastUsed, setContrastUsed] = useState(false);
    const [contrastAgent, setContrastAgent] = useState('');
    const [technique, setTechnique] = useState('');
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (!open || !row) return;
        // Pre-ticked from the procedure master only as a PROMPT — the
        // radiographer confirms what was actually given. Left unticked for a
        // procedure that does not normally use contrast, so the common case is
        // one press.
        setContrastUsed(Boolean(row.requiresContrast));
        setContrastAgent('');
        setTechnique('');
        setRemarks('');
    }, [open, row]);

    if (!open || !row) return null;

    const agentMissing = contrastUsed && !contrastAgent.trim();

    return (
        <div
            data-testid="perform-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            role="dialog"
            aria-modal="true"
        >
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-start justify-between gap-3 px-5 py-4 bg-slate-900 text-white">
                    <div>
                        <h2 className="text-lg font-bold">{t('imagingworklist.perform_title', 'Record this study as performed')}</h2>
                        <p className="text-xs text-white/70 mt-0.5">
                            {[row.accessionNo, row.patientName || t('common.patient_label', 'Patient'), row.procedureName].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="perform-modal-close"
                        onClick={onClose}
                        className="p-1 rounded-lg hover:bg-white/20 cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <p className="text-xs text-slate-500">
                        {t(
                            'imagingworklist.perform_hint',
                            'This says a person was put through a machine. Nothing returns from it — a study recorded in error cannot be un-performed from any screen.'
                        )}
                    </p>

                    <label className="flex items-start gap-2">
                        <input
                            data-testid="perform-contrast"
                            type="checkbox"
                            checked={contrastUsed}
                            onChange={(e) => setContrastUsed(e.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-[#2D8A8A] cursor-pointer"
                        />
                        <span className="text-sm text-slate-700">
                            {t('imagingworklist.contrast_used', 'Contrast was given')}
                            <span className="block text-[11px] text-slate-400">
                                {t('imagingworklist.contrast_hint', 'Iodinated contrast is nephrotoxic and gadolinium is restricted in renal failure. A reaction has to be traceable to what was injected.')}
                            </span>
                        </span>
                    </label>

                    {contrastUsed && (
                        <label className="block">
                            <span className="text-xs font-semibold text-slate-600">
                                {t('imagingworklist.contrast_agent', 'Which agent, and how much')}
                            </span>
                            <input
                                data-testid="perform-contrast-agent"
                                type="text"
                                value={contrastAgent}
                                maxLength={120}
                                onChange={(e) => setContrastAgent(e.target.value.replace(/[<>]/g, ''))}
                                placeholder={t('imagingworklist.contrast_placeholder', 'e.g. Iohexol 350, 80 mL IV')}
                                className={`mt-1 w-full p-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${agentMissing ? 'border-red-300' : 'border-gray-300'}`}
                            />
                            {agentMissing && (
                                <span className="text-[11px] text-red-600 mt-1 inline-block">
                                    {t('imagingworklist.contrast_required', 'Name the agent — “contrast was used” is not an answer to which.')}
                                </span>
                            )}
                        </label>
                    )}

                    <label className="block">
                        <span className="text-xs font-semibold text-slate-600">{t('imagingworklist.technique', 'Technique')}</span>
                        <textarea
                            data-testid="perform-technique"
                            rows={2}
                            value={technique}
                            maxLength={500}
                            onChange={(e) => setTechnique(e.target.value)}
                            placeholder={t('imagingworklist.technique_placeholder', 'e.g. PA erect, 90 kVp; or axial 5 mm, portal venous phase')}
                            className="mt-1 w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        />
                        <span className="text-[11px] text-slate-400">
                            {t('imagingworklist.technique_hint', 'What the console actually did. The radiologist reports from it, and the report prints it.')}
                        </span>
                    </label>

                    <label className="block">
                        <span className="text-xs font-semibold text-slate-600">{t('imagingworklist.remarks', 'Remarks')}</span>
                        <input
                            data-testid="perform-remarks"
                            type="text"
                            value={remarks}
                            maxLength={200}
                            onChange={(e) => setRemarks(e.target.value.replace(/[<>]/g, ''))}
                            placeholder={t('imagingworklist.remarks_placeholder', 'e.g. patient could not lie flat, films degraded by movement')}
                            className="mt-1 w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        />
                    </label>
                </div>

                <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl border border-gray-300 text-slate-600 text-sm font-semibold hover:bg-white cursor-pointer"
                    >
                        {t('imagingworklist.cancel', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        data-testid="perform-confirm"
                        disabled={busy || agentMissing}
                        onClick={() => onConfirm({
                            contrastUsed,
                            contrastAgent: contrastAgent.trim(),
                            technique: technique.trim(),
                            remarks: remarks.trim(),
                        })}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-bold shadow-lg ${busy || agentMissing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-800'}`}
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4 text-[#4ABDBD]" />}
                        {t('imagingworklist.perform_confirm', 'Record as performed')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export default function ImagingWorklist() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can, canAny } = usePermissions();
    const session = useSession();
    const [searchParams] = useSearchParams();

    // WHOSE DEPARTMENT. A doctor, an admin or an employee carries their hospital
    // in the verified token and cannot name another, so what is sent here is
    // ignored for them. Super Admin is unscoped everywhere in this codebase and
    // holds no tenant at all — every one of these endpoints answers 400 until the
    // organisation is named, which is what ?tenantId= on the URL is for. Passing
    // it unconditionally is what lets one screen serve both instead of growing a
    // second code path only Super Admin ever exercises.
    const tenantId = searchParams.get('tenantId') || session.tenantId || '';

    const canBook = can('radiology.schedule');
    const canPerform = can('radiology.study.perform');
    // The form is the one place a DOCTOR must not be locked out of. The Act
    // allows only a registered practitioner to conduct a pre-natal diagnostic
    // procedure, and the sonologist doing the obstetric scan is very often a
    // doctor — who holds no staff permission whatever. The route takes the same
    // position (see api/routes/radiologyWorkflowRoutes.js): a gate that stops
    // them completing the form stops the lawful scan, and a department that
    // cannot complete the form under pressure performs without it.
    const canWriteFormF = canAny([
        'radiology.study.perform',
        'radiology.report.draft',
        'radiology.report.sign',
    ]) || session.isDoctor;

    const [bucket, setBucket] = useState('scheduled');
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshedAt, setRefreshedAt] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [date, setDate] = useState(() => todayLocal());
    // Whether the day above is applied at all, as an OVERRIDE of the bucket's own
    // default rather than as a copy of it: null means "follow the bucket". See the
    // header — a booked list is a day's work, an unbooked request or an unreported
    // study is a backlog, and a backlog bounded by a date hides itself.
    //
    // Deliberately not an effect that rewrites a boolean when the tab changes. That
    // version fires a second fetch on every tab press — the first with the previous
    // tab's date rule — so the department sees the wrong list flash past on a screen
    // whose whole job is to be believed.
    const [dateOverride, setDateOverride] = useState(null);
    const [modality, setModality] = useState('');
    const [equipment, setEquipment] = useState([]);
    const [equipmentError, setEquipmentError] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE);
    const [refreshKey, setRefreshKey] = useState(0);

    const [scheduleRow, setScheduleRow] = useState(null);
    const [performRow, setPerformRow] = useState(null);
    const [busyStudyId, setBusyStudyId] = useState(null);

    // Paused while a dialog is open or an action is in flight. A poll landing
    // mid-booking would swap the row out from under the slot picker and leave a
    // clerk confirming a time for whoever the list moved into that position.
    // Held in a ref so the interval reads the current answer without being torn
    // down and rebuilt — a poll that restarts on every state change never fires.
    const pollPausedRef = useRef(false);
    useEffect(() => {
        pollPausedRef.current = Boolean(scheduleRow || performRow || busyStudyId);
    }, [scheduleRow, performRow, busyStudyId]);

    // Built once rather than per request: the instance reads the access token in
    // its request interceptor, so a single memoised copy stays correct across a
    // token refresh.
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    /* ---- the department's machines, for the slot picker ---- */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Only the machines that are in service. A scanner out for a tube
                // change or one that has been retired is not a slot anybody can be
                // given, and offering it produces a booking the server refuses
                // with the patient standing there.
                const res = await axiosInstance.get(EQUIPMENT_API, {
                    params: { status: 'active', limit: FLEET_LIMIT, tenantId },
                });
                if (cancelled) return;
                setEquipment(res?.data?.data || []);
                setEquipmentError(null);
            } catch (err) {
                // Kept rather than toasted. It blocks nothing on the list itself,
                // and the only place it matters is inside the slot picker — which
                // says so there, where somebody is actually trying to book.
                if (cancelled) return;
                setEquipment([]);
                setEquipmentError(extractErrorMessage(err, 'Could not load the machines'));
            }
        })();
        return () => { cancelled = true; };
    }, [axiosInstance, tenantId]);

    /* ---- search ---- */
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [searchQuery]);

    // A search is a hunt for one named person or one accession number, and the
    // study being looked for is usually the one that did NOT happen today — that
    // is why somebody is searching. So the day filter is dropped while a term is
    // present, and the screen says so rather than quietly returning nothing.
    const searching = debouncedSearch.length > 0;
    // The bucket's default, unless somebody has said otherwise on this tab. A
    // default rather than a lock: a desk that wants Thursday's list, or every
    // unbooked request from the last three months, can have either.
    const dateOn = dateOverride === null ? DAY_LIST_BUCKETS.includes(bucket) : dateOverride;
    const dateApplies = dateOn && !searching;

    const load = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        try {
            const body = await dispatch(getRadiologyWorklist({
                // The backend takes a queue id OR a bare status; these five tabs
                // are statuses, because 'scheduled' and 'arrived' owe different
                // acts and its 'to_perform' queue holds both.
                queue: bucket,
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearch,
                date: dateApplies ? date : '',
                modality,
                tenantId,
            })).unwrap();
            setRows(body?.data || []);
            setCounts(body?.counts?.byStatus || {});
            setTotal(toNum(body?.total));
            setTruncated(Boolean(body?.truncated));
            setError(null);
            setRefreshedAt(new Date());
        } catch (err) {
            setError(extractErrorMessage(err, t('imagingworklist.load_error', 'Could not load the worklist')));
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [dispatch, bucket, currentPage, itemsPerPage, debouncedSearch, dateApplies, date, modality, tenantId, t]);

    useEffect(() => { load(); }, [load, refreshKey]);

    // The department is a shared room: somebody else marks the patient arrived,
    // and the row has to move without anybody pressing anything. Quiet, so the
    // table never blinks — a spinner every twenty seconds on a screen that is
    // open all day is how people stop reading it.
    useEffect(() => {
        const id = setInterval(() => {
            if (!pollPausedRef.current) load({ quiet: true });
        }, REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    const refetch = () => setRefreshKey((k) => k + 1);

    /* -------------------------------------------------------------- *
     * Actions
     * -------------------------------------------------------------- */

    /**
     * A refusal the department can act on, shown as a dialog rather than a toast.
     *
     * None of these is "something went wrong": a lapsed AERB certificate is a
     * renewal somebody has to chase, a taken slot is another time to offer the
     * patient, a missing Form F is a form to complete before the scan. A toast
     * slides away while the radiographer is looking at the console.
     */
    const explainRefusal = async (err, fallback, row) => {
        const code = err?.code;
        const message = extractErrorMessage(err, fallback);

        if (code === 'PNDT_FORM_REQUIRED') {
            const go = await MySwal.fire({
                title: t('imagingworklist.pndt_block_title', 'Form F is not complete'),
                text: t(
                    'imagingworklist.pndt_block_text',
                    'This scan is within the PC-PNDT Act, so it cannot be recorded as performed until Form F exists with the declaration accepted, the performing doctor’s registration number and the referring doctor named. Non-compliance here is a criminal offence, not a fine.'
                ),
                icon: 'error',
                showCancelButton: true,
                confirmButtonColor: '#2D8A8A',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('imagingworklist.open_form_f', 'Open Form F'),
                cancelButtonText: t('imagingworklist.cancel', 'Cancel'),
            });
            if (go.isConfirmed && row?.studyId) navigate(formFPath(row.studyId));
            return;
        }

        const hardCodes = [
            'EQUIPMENT_AERB_EXPIRED', 'EQUIPMENT_AERB_MISSING',
            'EQUIPMENT_PNDT_EXPIRED', 'EQUIPMENT_PNDT_UNREGISTERED',
            'EQUIPMENT_RETIRED', 'EQUIPMENT_UNDER_MAINTENANCE', 'EQUIPMENT_NOT_AVAILABLE',
            'EQUIPMENT_MODALITY_MISMATCH', 'PNDT_DOCTOR_NOT_REGISTERED',
            'SIGNATORY_NOT_REGISTERED', 'ACTOR_UNKNOWN',
        ];
        if (hardCodes.includes(code)) {
            await MySwal.fire({
                title: t('imagingworklist.refused_title', 'This cannot be done yet'),
                text: message,
                icon: 'warning',
                confirmButtonColor: '#2D8A8A',
                confirmButtonText: t('imagingworklist.understood', 'Understood'),
            });
            return;
        }

        // A race is not an error the person made: somebody else moved the study a
        // second ago. Reload rather than inviting a second attempt on stale data.
        if (['SLOT_TAKEN', 'SCHEDULE_RACED', 'ARRIVE_RACED', 'PERFORM_RACED', 'ILLEGAL_TRANSITION'].includes(code)) {
            toast.warn(message);
            refetch();
            return;
        }

        toast.warn(message);
    };

    const handleSchedule = async ({ equipmentId, scheduledAt, time: slotTime }) => {
        const row = scheduleRow;
        // Shown only — the booking sends scheduledAt (slot.start), never this.
        const time = formatClock(slotTime);
        if (!row?.studyId || !equipmentId || !scheduledAt) return;
        setBusyStudyId(row.studyId);
        try {
            const body = await dispatch(scheduleStudy({
                studyId: row.studyId,
                equipmentId,
                scheduledAt,
                tenantId,
            })).unwrap();
            const accessionNo = body?.data?.accessionNo || body?.accessionNo;
            setScheduleRow(null);
            // The accession number is minted by this call and is what goes on the
            // film envelope and the request form, so it is read back rather than
            // left for somebody to find by reopening the row.
            toast.success(
                accessionNo
                    ? t('imagingworklist.booked_with_no', 'Booked {{time}} — accession {{no}}', { time, no: accessionNo })
                    : t('imagingworklist.booked', 'Booked {{time}}', { time })
            );
            refetch();
        } catch (err) {
            await explainRefusal(err, t('imagingworklist.schedule_error', 'Could not book this scan'), row);
        } finally {
            setBusyStudyId(null);
        }
    };

    const handleArrive = async (row) => {
        if (!row?.studyId) return;
        setBusyStudyId(row.studyId);
        try {
            await dispatch(arriveStudy({ studyId: row.studyId, tenantId })).unwrap();
            toast.success(t('imagingworklist.arrived', 'Patient marked as arrived'));
            refetch();
        } catch (err) {
            await explainRefusal(err, t('imagingworklist.arrive_error', 'Could not record the arrival'), row);
        } finally {
            setBusyStudyId(null);
        }
    };

    const handlePerform = async (payload) => {
        const row = performRow;
        if (!row?.studyId) return;
        setBusyStudyId(row.studyId);
        try {
            const body = await dispatch(performStudy({
                studyId: row.studyId,
                ...payload,
                tenantId,
            })).unwrap();
            setPerformRow(null);
            const accessionNo = body?.data?.accessionNo || row.accessionNo;
            toast.success(
                accessionNo
                    ? t('imagingworklist.performed_with_no', 'Recorded — {{no}} is now with the radiologist', { no: accessionNo })
                    : t('imagingworklist.performed', 'Study recorded as performed')
            );
            refetch();
        } catch (err) {
            await explainRefusal(err, t('imagingworklist.perform_error', 'Could not record this study'), row);
        } finally {
            setBusyStudyId(null);
        }
    };

    /**
     * Perform, asked for on a row where the Act applies and the form does not
     * exist. Refused HERE rather than at the server, with the reason in full and
     * a way straight to the form: a radiographer told only "not allowed", with a
     * patient on the table, is a radiographer who scans first and files later.
     */
    const blockedByPndt = async (row) => {
        const go = await MySwal.fire({
            title: t('imagingworklist.pndt_block_title', 'Form F is not complete'),
            html: `<div style="text-align:left;font-size:14px;color:#334155">
                <p style="margin-bottom:10px">${t(
                    'imagingworklist.pndt_block_html',
                    'This is an examination the PC-PNDT Act covers. It cannot be recorded as performed until Form F exists for it, carrying the declaration, the performing doctor’s registration number and the referring doctor.'
                )}</p>
                <p style="padding:8px 10px;border-radius:8px;background:#fef2f2;color:#991b1b;font-size:13px">${t(
                    'imagingworklist.pndt_block_penalty',
                    'Performing one without the form is a criminal offence — imprisonment and the sealing of the machine, not a fine.'
                )}</p>
            </div>`,
            icon: 'error',
            showCancelButton: true,
            confirmButtonColor: '#2D8A8A',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('imagingworklist.open_form_f', 'Open Form F'),
            cancelButtonText: t('imagingworklist.cancel', 'Cancel'),
        });
        if (go.isConfirmed) navigate(formFPath(row.studyId));
    };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    /* -------------------------------------------------------------- *
     * Per-row actions
     *
     * Drawn from `nextStatuses`, which the server computes from its own
     * transition table, intersected with what this user may do. A second copy of
     * that table here is how a screen ends up offering a control the endpoint
     * then refuses — which the department reads as a broken system rather than as
     * a rule.
     * -------------------------------------------------------------- */
    const primaryAction = (row) => {
        const busy = busyStudyId === row.studyId;
        const next = Array.isArray(row.nextStatuses) ? row.nextStatuses : [];
        const pndtBlocks = Boolean(row.pndtRequired && !row.pndtCleared);
        const buttons = [];

        if (next.includes('scheduled')) {
            buttons.push(
                canBook ? (
                    <button
                        key="schedule"
                        type="button"
                        data-testid={`schedule-${row.studyId}`}
                        disabled={busy}
                        onClick={() => setScheduleRow(row)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-xs font-semibold shadow ${busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                    >
                        <CalendarClock className="w-3.5 h-3.5" />
                        {row.scheduledAt
                            ? t('imagingworklist.reschedule', 'Move')
                            : t('imagingworklist.schedule', 'Book')}
                    </button>
                ) : (
                    <LockedAction
                        key="schedule"
                        testId={`locked-schedule-${row.studyId}`}
                        label={t('imagingworklist.schedule', 'Book')}
                        reason={t('imagingworklist.no_schedule_permission', 'Booking a machine is the department’s act and needs radiology.schedule')}
                    />
                )
            );
        }

        if (next.includes('arrived')) {
            buttons.push(
                canBook ? (
                    <button
                        key="arrive"
                        type="button"
                        data-testid={`arrive-${row.studyId}`}
                        disabled={busy}
                        onClick={() => handleArrive(row)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] text-xs font-semibold ${busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-[#4ABDBD]/10'}`}
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DoorOpen className="w-3.5 h-3.5" />}
                        {t('imagingworklist.arrive', 'Arrived')}
                    </button>
                ) : (
                    <LockedAction
                        key="arrive"
                        testId={`locked-arrive-${row.studyId}`}
                        label={t('imagingworklist.arrive', 'Arrived')}
                        reason={t('imagingworklist.no_schedule_permission', 'Booking a machine is the department’s act and needs radiology.schedule')}
                    />
                )
            );
        }

        if (next.includes('performed')) {
            if (!canPerform) {
                buttons.push(
                    <LockedAction
                        key="perform"
                        testId={`locked-perform-${row.studyId}`}
                        label={t('imagingworklist.perform', 'Perform')}
                        reason={t('imagingworklist.no_perform_permission', 'Recording an exposure is the radiographer’s half of the two-person rule and needs radiology.study.perform')}
                    />
                );
            } else if (pndtBlocks) {
                // Not disabled and not hidden: a red control that explains itself
                // and leads to the form. A greyed-out button with no reason is
                // what gets worked around.
                buttons.push(
                    <button
                        key="perform"
                        type="button"
                        data-testid={`perform-blocked-${row.studyId}`}
                        onClick={() => blockedByPndt(row)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 text-white text-xs font-bold shadow cursor-pointer hover:bg-red-700"
                    >
                        <ShieldAlert className="w-3.5 h-3.5" />
                        {t('imagingworklist.form_f_first', 'Form F first')}
                    </button>
                );
            } else {
                buttons.push(
                    <button
                        key="perform"
                        type="button"
                        data-testid={`perform-${row.studyId}`}
                        disabled={busy}
                        onClick={() => setPerformRow(row)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-bold shadow ${busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-800'}`}
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5 text-[#4ABDBD]" />}
                        {t('imagingworklist.perform', 'Perform')}
                    </button>
                );
            }
        }

        // The form itself, reachable from every row the Act touches — before the
        // scan to complete it, after it to read what was declared.
        if (row.pndtRequired && canWriteFormF) {
            buttons.push(
                <button
                    key="formf"
                    type="button"
                    data-testid={`formf-${row.studyId}`}
                    onClick={() => navigate(formFPath(row.studyId))}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-semibold cursor-pointer ${row.pndtCleared ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'}`}
                >
                    <Stamp className="w-3.5 h-3.5" />
                    {row.pndtCleared ? t('imagingworklist.view_form_f', 'Form F') : t('imagingworklist.fill_form_f', 'Fill Form F')}
                </button>
            );
        }

        if (!buttons.length) {
            // 'performed' and 'reported' rows: this screen is not where a report
            // is written or signed. Saying what is owed and by whom is more use
            // than a button that would take somebody to a screen they cannot use.
            return (
                <span className="text-[11px] text-slate-400">
                    {row.status === 'performed'
                        ? t('imagingworklist.awaiting_report', 'With the radiologist')
                        : row.status === 'reported'
                            ? t('imagingworklist.awaiting_signature', 'Awaiting signature')
                            : '—'}
                </span>
            );
        }

        return <div className="flex flex-wrap items-center justify-center gap-2">{buttons}</div>;
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    // Counted from the rows on screen rather than from a department-wide figure,
    // for the reason the laboratory learned the hard way: `counts` holds the
    // per-status sizes and nothing else, so a banner reading counts.pndt would
    // silently never appear — on the one screen where silence is the worst
    // possible failure. The wording says "on this page" rather than claiming a
    // total the screen cannot see.
    const pndtBlockedOnPage = rows.filter((r) => r.pndtRequired && !r.pndtCleared).length;

    /* -------------------------------------------------------------- *
     * Render
     * -------------------------------------------------------------- */

    return (
        <div data-testid="imaging-worklist-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div className="ml-1">
                    <h1
                        data-testid="imaging-worklist-title"
                        className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                    >
                        {t('imagingworklist.title', 'Imaging worklist')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('imagingworklist.subtitle', 'What the department owes today, and what has to happen to each of it next.')}
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {refreshedAt && (
                        <span className="text-[11px] text-slate-400 hidden sm:inline">
                            {t('imagingworklist.updated_at', 'Updated {{time}}', { time: fmtClock(refreshedAt.toISOString()) })}
                        </span>
                    )}
                    <button
                        type="button"
                        data-testid="imaging-worklist-refresh"
                        onClick={refetch}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 cursor-pointer whitespace-nowrap"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        {t('imagingworklist.refresh', 'Refresh')}
                    </button>
                </div>
            </div>

            {/* The five places a patient can be, with their counts. */}
            <div className="mb-4 overflow-x-auto">
                <div className="relative inline-flex min-w-full sm:min-w-0 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-1">
                    {BUCKETS.map((b) => {
                        const Icon = b.icon;
                        const active = bucket === b.id;
                        const n = toNum(counts[b.id]);
                        return (
                            <button
                                key={b.id}
                                type="button"
                                data-testid={`imaging-bucket-${b.id}`}
                                title={t(`imagingworklist.hint_${b.id}`, BUCKET_HINTS[b.id])}
                                onClick={() => {
                                    setBucket(b.id);
                                    // Back to the new tab's own rule about the day
                                    // filter: carrying "show everything owed" from
                                    // the unbooked queue onto the booked list would
                                    // silently widen a day's work to three months.
                                    setDateOverride(null);
                                    setCurrentPage(1);
                                }}
                                className={`relative px-4 py-2 text-sm rounded-xl transition-colors duration-300 z-10 whitespace-nowrap ${active ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                            >
                                {active && (
                                    <motion.div
                                        layoutId="activeImagingBucket"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20 inline-flex items-center gap-2">
                                    <Icon className="w-4 h-4" />
                                    {t(`imagingworklist.bucket_${b.id}`, b.fallback)}
                                    <span
                                        className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold ${active ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-600'}`}
                                    >
                                        {n}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* PC-PNDT outranks every filter on this page. */}
            {pndtBlockedOnPage > 0 && (
                <div
                    data-testid="pndt-banner"
                    className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-red-50 border-2 border-red-300"
                >
                    <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                        <span className="font-bold">
                            {t('imagingworklist.pndt_count', '{{n}} scan(s) on this page need Form F before they can be performed', { n: pndtBlockedOnPage })}
                        </span>
                        <p className="text-xs text-red-700 mt-0.5">
                            {t(
                                'imagingworklist.pndt_banner_hint',
                                'An obstetric or pelvic ultrasound on a female patient is within the PC-PNDT Act. The form needs the declaration, the performing doctor’s registration number and the referring doctor, and the record is kept two years. Performing without it is a criminal offence.'
                            )}
                        </p>
                    </div>
                </div>
            )}

            <div className="mb-5 flex flex-col sm:flex-row flex-wrap gap-2 sm:items-center">
                <div className="relative w-full sm:w-80">
                    <input
                        data-testid="imaging-worklist-search"
                        type="text"
                        placeholder={t('imagingworklist.search_placeholder', 'Accession no / patient / procedure…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                        maxLength={50}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div className="relative w-full sm:w-48">
                    <input
                        data-testid="imaging-worklist-date"
                        type="date"
                        value={date}
                        disabled={!dateApplies}
                        title={dateApplies
                            ? undefined
                            : t('imagingworklist.date_off_hint', 'The whole outstanding list is on screen, not one day of it')}
                        onChange={(e) => { setDate(e.target.value); setCurrentPage(1); }}
                        className={`w-full p-2 pl-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${dateApplies ? '' : 'opacity-40 cursor-not-allowed'}`}
                    />
                    <CalendarDays className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                {/* The switch between "one day's list" and "everything still
                    owed", stated rather than inferred. A person who cannot see
                    which is in force cannot tell a missing row from a filtered
                    one. */}
                <button
                    type="button"
                    data-testid="imaging-date-toggle"
                    disabled={searching}
                    onClick={() => { setDateOverride(!dateOn); setCurrentPage(1); }}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-semibold whitespace-nowrap border ${searching ? 'opacity-40 cursor-not-allowed border-slate-200 text-slate-400' : 'cursor-pointer border-[#4ABDBD] text-[#2D8A8A] hover:bg-[#4ABDBD]/10'}`}
                >
                    <Clock className="w-3.5 h-3.5" />
                    {dateApplies
                        ? t('imagingworklist.showing_one_day', 'Showing one day — show everything owed')
                        : t('imagingworklist.showing_backlog', 'Showing everything owed — show one day')}
                </button>

                <select
                    data-testid="imaging-worklist-modality"
                    value={modality}
                    onChange={(e) => { setModality(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-44 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('imagingworklist.all_modalities', 'Every modality')}</option>
                    {MODALITIES.map((m) => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>

                {searching && (
                    <span
                        data-testid="searching-window"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold whitespace-nowrap"
                    >
                        <Clock className="w-3.5 h-3.5" />
                        {t('imagingworklist.searching_window', 'Searching the last {{n}} days, not one day', { n: BACKLOG_WINDOW_DAYS })}
                    </span>
                )}
            </div>

            {error && (
                <div data-testid="imaging-worklist-error" className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {/* The window itself was capped. Said out loud: a list that quietly
                stops is a list somebody trusts to be complete. */}
            {truncated && (
                <div data-testid="imaging-truncated" className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 text-xs">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {t('imagingworklist.truncated', 'There is more work in this window than one screen can hold. Narrow it by day, modality or search — rows are being left out.')}
                </div>
            )}

            {loading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('imagingworklist.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    {/* Table on the reception monitor; cards on the radiographer's phone. */}
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="imaging-worklist-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 pl-2 text-left text-xs font-bold uppercase tracking-wider w-12 border-l-4 border-transparent">#</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingworklist.accession', 'Accession')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingworklist.patient', 'Patient')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingworklist.examination', 'Examination')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingworklist.machine', 'Machine')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingworklist.timing', 'Timing')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('imagingworklist.next_step', 'Next step')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="imaging-worklist-body">
                                {rows.length > 0 ? rows.map((row, index) => {
                                    const priority = String(row.priority || 'routine').toLowerCase();
                                    const style = PRIORITY_STYLES[priority];
                                    const mins = minutesUntil(row.scheduledAt);
                                    return (
                                        <tr
                                            key={row.studyId}
                                            data-testid={`imaging-row-${row.studyId}`}
                                            className={`border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/60 hover:to-cyan-50/60 transition-all duration-300 ${style?.row || ''}`}
                                        >
                                            {/* The priority rail. Colour is the third coding, not the first. */}
                                            <td className={`p-3 pl-2 text-xs text-gray-500 border-l-4 ${style?.railBorder || 'border-transparent'}`}>
                                                {indexOfFirst + index + 1}
                                            </td>

                                            <td className="p-3">
                                                <div className="flex flex-col items-start gap-1.5">
                                                    <AccessionBadge accessionNo={row.accessionNo} testId={`accession-${row.studyId}`} />
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <PriorityBadge priority={priority} />
                                                        <PndtBadge row={row} />
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="font-semibold text-gray-700">{row.patientName || t('common.patient_label', 'Patient')}</div>
                                                <div className="text-[11px] text-slate-400">
                                                    {[row.patientAge, row.patientGender, hospitalPatientCode(row)].filter(Boolean).join(' · ')}
                                                </div>
                                                {row.doctorName && (
                                                    <div className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
                                                        <Stethoscope className="w-3 h-3" />
                                                        {row.doctorName}
                                                    </div>
                                                )}
                                            </td>

                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="font-semibold text-gray-700">{row.procedureName || '—'}</div>
                                                <div className="text-[11px] text-slate-400">
                                                    {[row.modality, row.bodyPart].filter(Boolean).join(' · ')}
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {row.requiresContrast && (
                                                        <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-bold uppercase tracking-wide">
                                                            {t('imagingworklist.contrast', 'Contrast')}
                                                        </span>
                                                    )}
                                                    {toNum(row.imagesHeld) > 0 && (
                                                        <span
                                                            title={t('imagingworklist.images_hint', 'Images held against this study')}
                                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold"
                                                        >
                                                            <Images className="w-3 h-3" />
                                                            {toNum(row.imagesHeld)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="p-3 text-xs text-gray-600">
                                                {row.equipmentName ? (
                                                    <>
                                                        <div className="font-semibold text-gray-700">{row.equipmentName}</div>
                                                        {row.room && (
                                                            <div className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                                                                <MapPin className="w-3 h-3" />
                                                                {row.room}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-[11px] text-slate-400">
                                                        {t('imagingworklist.no_machine', 'No machine held')}
                                                    </span>
                                                )}
                                            </td>

                                            <td className="p-3 text-xs text-gray-600">
                                                {row.scheduledAt ? (
                                                    <>
                                                        <div className="inline-flex items-center gap-1 font-semibold text-slate-700">
                                                            <Clock className="w-3 h-3" />
                                                            {fmtClock(row.scheduledAt)}
                                                        </div>
                                                        <div className="text-[11px] text-slate-400">{fmtDay(row.scheduledAt)}</div>
                                                        {mins !== null && row.status === 'scheduled' && (
                                                            <div className={`text-[11px] font-semibold mt-0.5 ${mins < 0 ? 'text-red-600' : mins < 30 ? 'text-amber-600' : 'text-slate-400'}`}>
                                                                {mins < 0
                                                                    ? t('imagingworklist.late_by', 'Late by {{d}}', { d: fmtDelay(mins) })
                                                                    : t('imagingworklist.due_in', 'In {{d}}', { d: fmtDelay(mins) })}
                                                            </div>
                                                        )}
                                                        {row.status === 'arrived' && row.arrivedAt && (
                                                            <div className="text-[11px] text-amber-600 font-semibold mt-0.5">
                                                                {t('imagingworklist.waiting_since', 'Waiting since {{time}}', { time: fmtClock(row.arrivedAt) })}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="text-[11px] text-slate-400">
                                                        {t('imagingworklist.ordered_at', 'Ordered {{when}}', { when: fmtDay(row.orderedAt) || '—' })}
                                                    </div>
                                                )}
                                            </td>

                                            <td className="p-3">
                                                <div className="flex justify-center">{primaryAction(row)}</div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={7} className="p-8 text-center text-gray-500 text-sm">
                                            {searching
                                                ? t('imagingworklist.no_match', 'Nothing matches that. Accession numbers look like R26-000123.')
                                                : t('imagingworklist.empty_bucket', 'Nothing here — everything at this stage has moved on.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards. The radiographer at the console is holding a phone. */}
                    <div className="block lg:hidden space-y-3">
                        {rows.length > 0 ? rows.map((row) => {
                            const priority = String(row.priority || 'routine').toLowerCase();
                            const style = PRIORITY_STYLES[priority];
                            const mins = minutesUntil(row.scheduledAt);
                            return (
                                <div
                                    key={row.studyId}
                                    data-testid={`imaging-card-${row.studyId}`}
                                    className="flex rounded-xl shadow-md overflow-hidden bg-gray-50"
                                >
                                    <span className={`w-1.5 ${style?.rail || 'bg-slate-200'}`} />
                                    <div className="flex-1 p-3">
                                        <div className="flex justify-between items-start gap-2">
                                            <AccessionBadge accessionNo={row.accessionNo} testId={`accession-card-${row.studyId}`} />
                                            <PriorityBadge priority={priority} />
                                        </div>
                                        <div className="mt-2 font-semibold text-gray-800 text-sm">
                                            {row.patientName || t('common.patient_label', 'Patient')}
                                        </div>
                                        <div className="text-[11px] text-slate-400">
                                            {[row.patientAge, row.patientGender, hospitalPatientCode(row)].filter(Boolean).join(' · ')}
                                        </div>
                                        <div className="mt-2 text-xs text-slate-600 font-semibold">
                                            {row.procedureName || '—'}
                                        </div>
                                        <div className="text-[11px] text-slate-400">
                                            {[row.modality, row.bodyPart, row.equipmentName, row.room].filter(Boolean).join(' · ')}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                            <PndtBadge row={row} />
                                            {row.scheduledAt && (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 font-semibold">
                                                    <Clock className="w-3 h-3" />
                                                    {`${fmtDay(row.scheduledAt)} ${fmtClock(row.scheduledAt)}`}
                                                </span>
                                            )}
                                            {mins !== null && mins < 0 && row.status === 'scheduled' && (
                                                <span className="text-[11px] font-semibold text-red-600">
                                                    {t('imagingworklist.late_by', 'Late by {{d}}', { d: fmtDelay(mins) })}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-3">{primaryAction(row)}</div>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {t('imagingworklist.empty_bucket', 'Nothing here — everything at this stage has moved on.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!loading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <SlotPicker
                open={Boolean(scheduleRow)}
                row={scheduleRow}
                equipment={equipment}
                equipmentError={equipmentError}
                busy={Boolean(busyStudyId)}
                tenantId={tenantId}
                onClose={() => setScheduleRow(null)}
                onConfirm={handleSchedule}
            />

            <PerformModal
                open={Boolean(performRow)}
                row={performRow}
                busy={Boolean(busyStudyId)}
                onClose={() => setPerformRow(null)}
                onConfirm={handlePerform}
            />
        </div>
    );
}
